import { supabase } from '../lib/supabase';

export interface FoodPortion {
  name_he: string;
  grams: number;
}

export interface Food {
  code: number;
  name_he: string;
  calories: number; // ל-100 גרם
  protein_g: number | null;
  carbs_g: number | null;
  fat_g: number | null;
  portions: FoodPortion[];
  custom?: boolean; // מאכל אישי של המשתמש
  id?: string;      // uuid של מאכל אישי (לעריכה); למאכלי המאגר אין
  /** אייקון שנבחר ידנית (מאכל אישי בלבד); null/חסר = ניחוש אוטומטי לפי שם */
  icon?: string | null;
}

export interface NewCustomFood {
  name_he: string;
  calories: number; // ל-100 גרם
  protein_g: number | null;
  carbs_g: number | null;
  fat_g: number | null;
  portions?: FoodPortion[]; // יחידת מנה אופציונלית (למשל קרקר = 30 ג')
  icon?: string | null;
}

/** יצירת מאכל אישי במאגר הפרטי — מחזיר את ה-id החדש (לשכפול-לעריכה, שממשיכה ישר לעמוד העריכה) */
export async function addCustomFood(food: NewCustomFood): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('no_session');
  const { data, error } = await supabase.from('litebite_custom_foods').insert({
    ...food, portions: food.portions ?? [], user_id: session.user.id,
  }).select('id').single();
  if (error) throw error;
  return (data as { id: string }).id;
}

/** רשימת כל המאכלים האישיים של המשתמש (לטאב "שלי" בחלונית החיפוש, לפני הקלדה) */
export async function listCustomFoods(limit = 50): Promise<Food[]> {
  const { data, error } = await supabase
    .from('litebite_custom_foods')
    .select('id, name_he, calories, protein_g, carbs_g, fat_g, portions, icon')
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) return [];
  return (data ?? []).map((r, i) => ({
    code: -1 - i,
    id: r.id as string,
    name_he: r.name_he,
    calories: Number(r.calories),
    protein_g: r.protein_g,
    carbs_g: r.carbs_g,
    fat_g: r.fat_g,
    portions: (r.portions as FoodPortion[]) ?? [],
    custom: true,
    icon: r.icon as string | null,
  }));
}

/** מאכל אישי בודד לפי id — לעריכה כשמגיעים אליו דרך מועדפים/היסטוריה (שאין בהם קישור-Food
 *  מוכן, רק customFoodId שנשמר על הפריט). null אם נמחק/לא נמצא (RLS מסננת לבעלים). */
export async function fetchCustomFoodById(id: string): Promise<Food | null> {
  const { data, error } = await supabase
    .from('litebite_custom_foods')
    .select('id, name_he, calories, protein_g, carbs_g, fat_g, portions, icon')
    .eq('id', id)
    .maybeSingle();
  if (error || !data) return null;
  return {
    code: -1,
    id: data.id as string,
    name_he: data.name_he,
    calories: Number(data.calories),
    protein_g: data.protein_g,
    carbs_g: data.carbs_g,
    fat_g: data.fat_g,
    portions: (data.portions as FoodPortion[]) ?? [],
    custom: true,
    icon: data.icon as string | null,
  };
}

/** נפילה לאחור לפריטי מועדפים/היסטוריה מלפני שנשמר customFoodId על הפריט: חיפוש מאכל אישי
 *  לפי שם מדויק (RLS מסננת לבעלים ממילא). מחזיר רק אם יש התאמה חד-משמעית — יותר משם אחד
 *  זהה, או אף אחד, לא מספיק בטוח כדי להניח שזה אותו מאכל בדיוק. */
export async function fetchCustomFoodByName(name: string): Promise<Food | null> {
  const { data, error } = await supabase
    .from('litebite_custom_foods')
    .select('id, name_he, calories, protein_g, carbs_g, fat_g, portions, icon')
    .eq('name_he', name);
  if (error || !data || data.length !== 1) return null;
  const r = data[0];
  return {
    code: -1,
    id: r.id as string,
    name_he: r.name_he,
    calories: Number(r.calories),
    protein_g: r.protein_g,
    carbs_g: r.carbs_g,
    fat_g: r.fat_g,
    portions: (r.portions as FoodPortion[]) ?? [],
    custom: true,
    icon: r.icon as string | null,
  };
}

/** מאכל בודד מהמאגר הלאומי לפי קוד — לעריכה/שכפול-לעריכה כשמגיעים דרך מועדפים/היסטוריה
 *  (שאין בהם קישור-Food מוכן, רק foodCode שנשמר על הפריט). null אם לא נמצא. */
export async function fetchNationalFoodByCode(code: number): Promise<Food | null> {
  const { data, error } = await supabase
    .from('litebite_foods')
    .select('code, name_he, calories, protein_g, carbs_g, fat_g, portions, icon')
    .eq('code', code)
    .maybeSingle();
  if (error || !data) return null;
  return {
    code: data.code as number,
    name_he: data.name_he,
    calories: Number(data.calories),
    protein_g: data.protein_g,
    carbs_g: data.carbs_g,
    fat_g: data.fat_g,
    portions: (data.portions as FoodPortion[]) ?? [],
    icon: data.icon as string | null,
  };
}

/** נפילה לאחור למועדפים/היסטוריה ישנים ממש (מלפני שנשמר גם foodCode): חיפוש במאגר הלאומי
 *  לפי שם מדויק — כמו fetchCustomFoodByName, רק רק אם יש התאמה חד-משמעית. */
export async function fetchNationalFoodByName(name: string): Promise<Food | null> {
  const { data, error } = await supabase
    .from('litebite_foods')
    .select('code, name_he, calories, protein_g, carbs_g, fat_g, portions, icon')
    .eq('name_he', name);
  if (error || !data || data.length !== 1) return null;
  const r = data[0];
  return {
    code: r.code as number,
    name_he: r.name_he,
    calories: Number(r.calories),
    protein_g: r.protein_g,
    carbs_g: r.carbs_g,
    fat_g: r.fat_g,
    portions: (r.portions as FoodPortion[]) ?? [],
    icon: r.icon as string | null,
  };
}

/** ערכי מאכל לעריכה (ל-100 גרם). icon רלוונטי רק למאכל אישי — לא קיים ב-litebite_foods הלאומי. */
export interface FoodPatch {
  name_he: string;
  calories: number;
  protein_g: number | null;
  carbs_g: number | null;
  fat_g: number | null;
  portions: FoodPortion[];
  icon?: string | null;
}

/** עדכון מאכל אישי (הבעלים בלבד — RLS owner_all) */
export async function updateCustomFood(id: string, patch: FoodPatch): Promise<void> {
  const { data, error } = await supabase
    .from('litebite_custom_foods')
    .update(patch)
    .eq('id', id)
    .select('id');
  if (error) throw error;
  if (!data || data.length === 0) throw new Error('not_found');
}

/** עדכון מאכל מהמאגר הלאומי (אדמין בלבד — policy במיגרציה 008) */
export async function updateNationalFood(code: number, patch: FoodPatch): Promise<void> {
  const { data, error } = await supabase
    .from('litebite_foods')
    .update(patch)
    .eq('code', code)
    .select('code');
  if (error) throw error;
  if (!data || data.length === 0) throw new Error('not_found');
}

/** חיפוש במאגר האישי — מוצג ראשון בתוצאות (מסומן custom) */
async function searchCustomFoods(q: string): Promise<Food[]> {
  let query = supabase
    .from('litebite_custom_foods')
    .select('id, name_he, calories, protein_g, carbs_g, fat_g, portions, icon');
  for (const w of searchTokens(q)) {
    query = query.ilike('name_he', `%${w}%`); // כל מילה חייבת להופיע (AND)
  }
  const { data, error } = await query.limit(8);
  if (error) return [];
  return (data ?? []).map((r, i) => ({
    code: -1 - i, // מזהה שלילי כדי לא להתנגש בקודי המאגר הלאומי
    id: r.id as string,
    name_he: r.name_he,
    calories: Number(r.calories),
    protein_g: r.protein_g,
    carbs_g: r.carbs_g,
    fat_g: r.fat_g,
    portions: (r.portions as FoodPortion[]) ?? [],
    custom: true,
    icon: r.icon as string | null,
  }));
}

/**
 * פירוק שאילתה למילות חיפוש. מילה בת תו אחד נשמרת אם היא ספרה —
 * "עמק 9" חייב לחפש גם את ה-9 (זריקתה הפכה את החיפוש ל"עמק" בלבד).
 */
function searchTokens(q: string): string[] {
  return q.split(/\s+/).filter((w) => w.length >= 2 || /\d/.test(w));
}

export interface SearchResult {
  foods: Food[];
  /** כל תוצאות המאגר הגיעו מדמיון-מילים (אין התאמה מדויקת) — ה-UI מציג הערה */
  fuzzyOnly: boolean;
}

/**
 * חיפוש במאגר המזון הישראלי — דרך RPC ‏litebite_search_foods (מיגרציה 009):
 * שלב מדויק (AND-ilike) + נפילה לדמיון-מילים pg_trgm כשאין מספיק ("יוגרט"→"יוגורט").
 * מיון בצד הלקוח: מדויק לפני דומה → ביטוי מלא → תחילת השם → שמות קצרים.
 */
export async function searchFoods(query: string, limit = 12): Promise<SearchResult> {
  const q = query.trim();
  // סף 3 תווים: אינדקס הטריגרמות דורש ≥3 תווים; מתחת לזה זו סריקה מלאה מיותרת
  if (q.length < 3) return { foods: [], fuzzyOnly: false };

  interface RpcRow {
    code: number; name_he: string; calories: number;
    protein_g: number | null; carbs_g: number | null; fat_g: number | null;
    portions: FoodPortion[] | null; icon: string | null; is_fuzzy: boolean;
  }
  const [nationalRes, customFoods] = await Promise.all([
    supabase.rpc('litebite_search_foods', { q, max_results: 40 }),
    searchCustomFoods(q),
  ]);
  if (nationalRes.error) throw nationalRes.error;
  const rows = (nationalRes.data ?? []) as RpcRow[];
  const fuzzyOnly = rows.length > 0 && rows.every((r) => r.is_fuzzy);
  const fuzzyOf = new Map(rows.map((r) => [r.code, r.is_fuzzy]));
  const national: Food[] = rows.map((r) => ({
    code: r.code,
    name_he: r.name_he,
    calories: Number(r.calories),
    protein_g: r.protein_g,
    carbs_g: r.carbs_g,
    fat_g: r.fat_g,
    portions: r.portions ?? [],
    icon: r.icon,
  }));
  national.sort((a, b) => {
    // מדויק תמיד לפני דומה
    const aFuzzy = fuzzyOf.get(a.code) ? 1 : 0;
    const bFuzzy = fuzzyOf.get(b.code) ? 1 : 0;
    if (aFuzzy !== bFuzzy) return aFuzzy - bFuzzy;
    // שם שמכיל את הביטוי המלא (למשל "עמק 9") גובר על התאמת מילים מפוזרות
    const aPhrase = a.name_he.includes(q) ? 0 : 1;
    const bPhrase = b.name_he.includes(q) ? 0 : 1;
    if (aPhrase !== bPhrase) return aPhrase - bPhrase;
    const aStarts = a.name_he.startsWith(q) ? 0 : 1;
    const bStarts = b.name_he.startsWith(q) ? 0 : 1;
    if (aStarts !== bStarts) return aStarts - bStarts;
    return a.name_he.length - b.name_he.length;
  });
  // מאכלים אישיים ראשונים, ואז המאגר הלאומי
  return { foods: [...customFoods, ...national].slice(0, limit), fuzzyOnly };
}
